import { useCallback, useEffect, useRef, useState } from "react";
import { probeGpu, formatBytes, type GpuReport } from "./gpu";
import { MindClient } from "./llm/client";
import { DEFAULT_MODEL_KEY, SHIPPED_MODELS, modelByKey } from "./llm/catalog";
import type { ChatMessage } from "./llm/protocol";
import { EmbedClient } from "./lattice/embedClient";
import { loadPack, type LoadedPack } from "./lattice/packStore";
import { PACKS, DEFAULT_PACK_KEY, packByKey } from "./lattice/packs";
import { converse, DEFAULT_CONFIG, type ConverseConfig, type JustifyArm } from "./converse";
import { cacheUsage, clearWeightCache, willFit, type CacheUsage } from "./lattice/weightCache";
import { probeGpu as probeCaps, canRun, type GpuCaps } from "./llm/capability";
import { LoadingOverlay, type LoadingState } from "./LoadingOverlay";
import {
  proposeAgenda,
  runStagedDebate,
  DEBATE_DEFAULTS,
  type AgendaResult,
  type Speaker,
} from "./debate";
import { RELEVANCE_FLOOR, openAperture } from "./lattice/aperture";
import { buildImagePrompt } from "./pure/visualPrompt";
import { interpretScene } from "./visualise";
import { toDataURL } from "./llm/imageOut";
import { cleanDeliberation } from "./pure/deliberate";
import type { GenerationRecord } from "./llm/client";
import { AgentField, type Touch } from "./AgentField";
import { project2d, dequantizeAll, type Projection } from "./pure/projection";
import type { SearchResult } from "./pure/toolParse";

type Turn = { role: "user" | "assistant"; content: string };

/**
 * One thing that happened, as a person would describe it.
 *
 * `question` is what they were asked, `note` is one expert preparing privately,
 * `said` is one expert speaking. There is no fourth kind, and that is the
 * point: everything else the machine did is machinery, and machinery lives in
 * the lab.
 */
type StreamItem =
  | { kind: "question"; text: string }
  | { kind: "note" | "said"; text: string; persona: string; speakerKey: string };
type Provenance = { source: "prefetch" | "tool"; query?: string; results: SearchResult[]; ms: number };

/**
 * The three things this page can do, named as a person would name them.
 *
 * A mode rather than a pile of toggles, because the three are genuinely
 * exclusive: they use different models, different panels and different words in
 * the composer. The old design expressed "two experts" as a disclosure in the
 * sidebar and left the image path with no expression at all, reachable only
 * from a console probe that a production build strips out.
 */
type Mode = "ask" | "two" | "picture";

/**
 * One picture, and what it was drawn from.
 *
 * `packKey` null means the control arm: the visitor's words alone, no memory.
 * That arm is not decoration. The whole claim is that swapping the memory
 * changes the picture, and a claim like that is only legible next to the
 * picture the same weights drew without it.
 */
type Drawing = {
  label: string;
  packKey: string | null;
  subject: string;
  prompt: string;
  /** The visual detail the lattice contributed. Empty is a real answer. */
  added: string[];
  url: string;
  sec: number;
  /**
   * Present when the model INTERPRETED the memory rather than the lexicon
   * scanning it. Carries what it actually said and how much of the result
   * traces back to the facts, because an interpretation that owes nothing to
   * the memory is a picture wearing a lattice's name.
   */
  scene?: { raw: string; overlap: number };
};

export function MindPage() {
  const [gpu, setGpu] = useState<GpuReport | null>(null);
  const [modelKey, setModelKey] = useState(DEFAULT_MODEL_KEY);
  const [modelReady, setModelReady] = useState(false);
  const [busy, setBusy] = useState<null | "model" | "pack" | "gen">(null);
  const [loadMsg, setLoadMsg] = useState("");
  const [pct, setPct] = useState(0);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const [packKey, setPackKey] = useState(DEFAULT_PACK_KEY);
  const [pack, setPack] = useState<LoadedPack | null>(null);
  const [embedReady, setEmbedReady] = useState(false);

  const [turns, setTurns] = useState<Turn[]>([]);
  const [prov, setProv] = useState<Provenance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [cfg, setCfg] = useState<ConverseConfig>(DEFAULT_CONFIG);
  const [lastMs, setLastMs] = useState<number | null>(null);
  const [usage, setUsage] = useState<CacheUsage | null>(null);
  // Both pickers collapse. Five lattice cards plus three model cards is a tall
  // rail, and once a visitor has chosen they rarely change it mid-conversation.
  const [memoryOpen, setMemoryOpen] = useState(true);
  const [modelOpen, setModelOpen] = useState(true);

  /**
   * What is loading, for the floating overlay. Kept structured rather than as
   * one prose string, because the overlay needs to tell a real byte count from
   * a phase that has no honest percentage.
   */
  const [load, setLoad] = useState<LoadingState>({ phase: null, title: "", pct: 0 });

  /**
   * What this device can execute, asked once. Used to refuse a tier BEFORE the
   * download rather than after it: a model can be unrunnable because of a
   * missing kernel or a missing shader-f16, and neither is worth discovering
   * two gigabytes in.
   */
  const [caps, setCaps] = useState<GpuCaps | null>(null);

  /**
   * Which of the three things the page is doing.
   *
   * This replaces a `debateOn` disclosure toggle in the sidebar, which was a
   * second source of truth for "are we running a discussion" and could disagree
   * with the engine: an open panel over an idle run, or a closed one over a
   * live one. A mode cannot disagree with itself.
   */
  const [mode, setMode] = useState<Mode>("ask");

  /* ---- picture it: one lattice, drawn twice (Free's idea) ---- */
  /**
   * The image model is held SEPARATELY from the chat model, on purpose.
   *
   * Free's call, and the bench backs it: Janus grounds at 25% against Gemma 3
   * 1B's 90% with the correct facts in its context every time, so it is an image
   * engine and not a chat tier. It is `shipped: false` for that reason and does
   * not appear in the model picker. Keeping it on its own client means entering
   * and leaving picture mode never disturbs a loaded conversation.
   */
  const imageLlm = useRef<MindClient | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const [imgQuery, setImgQuery] = useState("");
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [drawStage, setDrawStage] = useState("");
  /**
   * A lattice that had nothing visual to say, named.
   *
   * Kept OUT of `error` on purpose. Declining is the property `visualPrompt.ts`
   * is built around: a grounding step that cannot decline is a corruption step.
   * Reporting it through the red error slot would tell a visitor that the
   * correct behaviour had failed.
   */
  const [declined, setDeclined] = useState<{ lattice: string; retrieved: number } | null>(null);
  /**
   * Draw the ungrounded control alongside.
   *
   * On by default because the comparison IS the demonstration. Offered as a
   * switch because it doubles the wait, and someone who has already seen the
   * point twice should not have to keep paying for it.
   */
  const [drawBare, setDrawBare] = useState(true);
  /**
   * Interpret the memory rather than scan it.
   *
   * ON by default, and that is a considered default rather than a lazy one. The
   * lexicon can only see appearance ADJECTIVES, so a lattice whose imagery
   * lives in its nouns has nothing to give it: hermes carries 11 distinct
   * visual words across 597 facts, 63 of them the "emerald" in Emerald Tablet.
   * Scanning is the right tool for a corpus of objects and the wrong one for a
   * corpus of ideas, and most of these lattices are the second kind.
   */
  const [interpret, setInterpret] = useState(true);

  const [debateBKey, setDebateBKey] = useState("");
  const [debateTopic, setDebateTopic] = useState("");
  const [debateRounds, setDebateRounds] = useState(DEBATE_DEFAULTS.rounds);
  const [debateDelay, setDebateDelay] = useState(DEBATE_DEFAULTS.delayMs);
  /**
   * The visitor-facing transcript, and it is deliberately NOT the lab's log.
   *
   * MACHINERY vs COGNITION is the whole design line. The lab shows the seams on
   * purpose: token counts, message arrays, the exact prompt off `input_ids`.
   * None of that belongs here. What does belong is the INTERIOR: that each
   * expert prepares privately before it speaks, that the preparation is its own
   * and not the other's, and that the two disagree because they remember
   * different things.
   *
   * So this stream carries four kinds of thing a person can follow, and nothing
   * a person would have to be taught to read.
   */
  const [debateStream, setDebateStream] = useState<StreamItem[]>([]);
  /** Who is working right now, and on what. Silence for six seconds reads as a hang. */
  const [debateNow, setDebateNow] = useState<{ persona: string; kind: string } | null>(null);
  const [debateWait, setDebateWait] = useState(0);
  const [debatePhase, setDebatePhase] = useState<"idle" | "agenda" | "picking" | "running">("idle");
  const [agenda, setAgenda] = useState<AgendaResult | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  /**
   * Show the private notes.
   *
   * ON by default, because the notes ARE the demonstration. A visitor who sees
   * only the spoken turns sees two chatbots taking it in turns; a visitor who
   * sees the preparation sees two memories being consulted. Off is offered
   * because some people want the argument and not the workings.
   */
  const [showNotes, setShowNotes] = useState(true);
  /**
   * Where each lattice's facts sit, and which of them each side reached for.
   *
   * Computed once per pack and kept: the layout is deterministic, so
   * recomputing it would burn 1.4s on the largest lattice to produce the same
   * picture. Keyed by pack rather than by side, since the same lattice in a
   * later discussion is the same memory.
   */
  const [fields, setFields] = useState<Record<string, Projection>>({});
  const [touches, setTouches] = useState<Record<string, Touch[]>>({});
  const [showField, setShowField] = useState(true);
  const [debating, setDebating] = useState(false);
  // A ref, not state: the running loop must see a stop the moment it is set,
  // and a state update would not reach the closure already in flight.
  const debateStop = useRef({ stopped: false });

  const llm = useRef<MindClient | null>(null);
  const embedder = useRef<EmbedClient | null>(null);
  const liveRef = useRef<HTMLDivElement | null>(null);
  const liveText = useRef("");
  const chatRef = useRef<HTMLDivElement | null>(null);

  const refreshUsage = useCallback(() => {
    void cacheUsage().then(setUsage);
  }, []);

  useEffect(() => {
    void probeGpu().then(setGpu);
    void probeCaps().then(setCaps);
    refreshUsage();
    return () => {
      llm.current?.dispose();
      embedder.current?.dispose();
      // The image engine is a third worker and is easy to forget. Left running
      // it holds 2.4 GB of GPU memory against a page that no longer exists.
      imageLlm.current?.dispose();
    };
  }, [refreshUsage]);

  const scrollDown = () => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  /* ------------------------------------------------------------ the pack */

  const openPack = useCallback(async (key: string) => {
    const entry = packByKey(key);
    if (!entry) return;
    setError(null);
    setBusy("pack");
    setPackKey(key);
    setPct(0);
    setLoadMsg(`Loading ${entry.persona}`);
    setLoad({ phase: "fetch", title: entry.persona, pct: 0 });

    try {
      const p = await loadPack(entry.url, {
        passphrase: entry.passphrase,
        onProgress: (loaded, total) => {
          setPct((loaded / total) * 100);
          setLoad({
            phase: "fetch",
            title: entry.persona,
            loaded,
            total,
            pct: total > 0 ? (loaded / total) * 100 : 0,
          });
        },
      });
      setLoad({ phase: "decode", title: entry.persona, pct: 100 });
      setPack(p);

      // The embedder is named by the pack, never by the app. That is what
      // makes a document/query prefix mismatch impossible rather than merely
      // unlikely.
      setLoadMsg(`Loading embedder for ${p.manifest.embedder.split("/").pop()}`);
      setLoad({ phase: "compile", title: p.manifest.embedder.split("/").pop() ?? "embedder", pct: 0 });
      embedder.current ??= new EmbedClient();
      // Packs ship Matryoshka-truncated, so the embedder's native width is
      // expected to be LARGER than the pack's. Only a smaller one is wrong,
      // and the aperture raises on that.
      const { dim } = await embedder.current.load(p.manifest.embedder, "webgpu", setLoadMsg);
      if (dim && dim < p.manifest.dim) {
        throw new Error(`embedder produced dim ${dim}, which is too small for this pack's ${p.manifest.dim}`);
      }
      setEmbedReady(true);
      setLoadMsg("");
      refreshUsage();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      setLoad({ phase: null, title: "", pct: 0 });
    }
  }, [refreshUsage]);

  /* ----------------------------------------------------------- the model */

  const openModel = useCallback(async (key: string) => {
    setError(null);
    const model = modelByKey(key);

    // Refuse before the download, not after. A model can be unrunnable for
    // reasons that have nothing to do with storage: a kernel this browser does
    // not implement, or a missing shader-f16 feature. Gemma 4 QAT taught us
    // this by spending 2.29 GB before failing.
    if (caps) {
      const verdict = canRun(model, caps);
      if (!verdict.runnable) {
        setError(verdict.reason ?? `${model.label} cannot run here.`);
        return;
      }
      if (verdict.warning) setError(verdict.warning);
    }

    // Ask what is actually left rather than assuming a ceiling. Quota is
    // derived from free disk on the browser profile's partition, so it differs
    // wildly between visitors and even between two browsers on one machine.
    const fit = await willFit(modelByKey(key).sizeBytes);
    if (!fit.fits) {
      setError(fit.warning ?? "not enough storage");
      return;
    }

    setBusy("model");
    setPct(0);
    setModelKey(key);
    setModelReady(false);
    setLoadMsg("Starting worker");
    setLoad({ phase: "fetch", title: model.label, pct: 0 });

    llm.current?.dispose();
    llm.current = new MindClient();
    /**
     * The transcript is DERIVED from the generation tap rather than pushed by
     * callbacks, and that is what keeps it honest: every call the model makes
     * goes through one place, so a stage that forgot to report itself is
     * impossible rather than merely unlikely. It also means debate.ts needed no
     * new plumbing beyond knowing when a question starts.
     *
     * `system` turns are skipped. That kind is the harness folding old notes to
     * fit the window, which is machinery by definition.
     */
    llm.current.onGeneration((rec: GenerationRecord) => {
      if (!rec.speaker || (rec.kind !== "think" && rec.kind !== "output")) return;
      const persona = rec.label?.split(":")[0]?.trim() || "";
      if (!rec.done) {
        setDebateNow({ persona, kind: rec.kind });
        return;
      }
      setDebateNow(null);
      /**
       * CLEANED THE SAME WAY THE PIPELINE CLEANS IT, and this was a real bug
       * caught on the first visitor-side run: the tap carries the RAW
       * generation, so a think that opened with a tool call rendered as
       * "Gardener is thinking: search: Mycorrhizal network communication in
       * tomato...". The pipeline strips that before it keeps the note, and the
       * visitor was being shown the one thing the machinery had removed, in the
       * column that is supposed to hold only cognition.
       *
       * `cleanDeliberation` is the same call debate.ts makes, so what appears
       * here is exactly the note the agent went on to use.
       */
      const raw = (rec.text ?? "").trim();
      const text =
        rec.kind === "think"
          ? cleanDeliberation(raw)
          // A spoken turn continuing an unfinished hand-off opens with an
          // ellipsis. `stripPrefill` removes it downstream; the tap sees it raw.
          : raw.replace(/^(…|\.\.\.|,)\s*/, "").trim();
      if (!text) return;
      setDebateStream((prev) => [
        ...prev,
        { kind: rec.kind === "think" ? "note" : "said", text, persona, speakerKey: rec.speaker! },
      ]);
      scrollDown();
    });
    // Once bytes stop arriving, the remaining wait is shader compilation, which
    // has no honest percentage. Tracking whether any progress has been seen is
    // what lets the overlay switch from a real bar to an indeterminate one at
    // the right moment.
    let sawBytes = false;
    try {
      const ms = await llm.current.load(key, {
        onMessage: (msg) => {
          setLoadMsg(msg);
          if (sawBytes) setLoad({ phase: "compile", title: model.label, pct: 100 });
        },
        onProgress: (file, loaded, total) => {
          sawBytes = true;
          setLoadMsg(`${file}  ${formatBytes(loaded)} / ${formatBytes(total)}`);
          setPct(total > 0 ? (loaded / total) * 100 : 0);
          setLoad({
            phase: "fetch",
            title: model.label,
            file,
            loaded,
            total,
            pct: total > 0 ? (loaded / total) * 100 : 0,
          });
        },
      });
      setLoadMs(ms);
      setModelReady(true);
      setLoadMsg("");
      refreshUsage();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      setLoad({ phase: null, title: "", pct: 0 });
    }
  }, [refreshUsage, caps]);

  /* ------------------------------------------------------- picture it */

  /**
   * Draw the subject twice: once from the visitor's words alone, once from the
   * words plus what this lattice remembers about them.
   *
   * THE ORDER HERE IS LOAD-BEARING, and it was measured rather than guessed.
   * Every retrieval finishes BEFORE 2.4 GB of image model becomes resident, so
   * the embedder and the image weights never compete for memory. Doing it the
   * obvious way round works on a 24 GB card and fails on a laptop.
   *
   * A lattice with nothing visual to say must contribute NOTHING and produce
   * the bare picture. `unchanged: true` is a result, not a failure, and it is
   * reported as one: the webdev lattice is 9,886 facts of prose about APIs and
   * a grounding step that cannot decline is a corruption step.
   */
  const draw = useCallback(async (subject: string) => {
    if (!pack || !embedder.current || drawing) return;
    setError(null);
    setDeclined(null);
    setDrawing(true);
    try {
      /* 1. RETRIEVE, while the image model is still on disk. */
      setDrawStage("looking through memory");
      // What to SEARCH for need not be what to DRAW: "damping-off" finds the
      // facts describing how a sick seedling looks, and nobody wants the words
      // "damping-off" rendered into the picture.
      const query = imgQuery.trim() || subject;
      const t0 = performance.now();
      const res = await openAperture(pack, embedder.current, query, 8);
      const facts = res.results.map((r) => [r.fact, r.quote].filter(Boolean).join(" "));
      // The literal reading is always computed: it is instant, it costs nothing,
      // and it is the fallback if the interpretation comes back unusable.
      const built = buildImagePrompt(subject, facts);

      // The same provenance panel the answers use. A picture is an assertion
      // too, and it is owed the same account of where it came from.
      setProv((prev) => [
        ...prev,
        { source: "prefetch", query, results: res.results, ms: performance.now() - t0 },
      ]);

      /* 2. LOAD THE IMAGE MODEL, only now, only if it is not resident, and
       *    only if there is actually going to be a picture.
       *
       *    That last guard is not hypothetical. With the control arm switched
       *    off and a lattice that declines, every branch below is skipped, and
       *    without this the page would spend 2.4 GB to draw nothing at all.
       *    Caught in testing by turning off a checkbox. */
      const janus = modelByKey("janus-pro-1b");
      // Interpreting needs the model loaded, so a lattice the lexicon would
      // have refused is still worth loading for. The guard only fires when
      // NEITHER path can produce a second picture.
      const willDraw = drawBare || !built.unchanged || (interpret && facts.length > 0);
      if (!willDraw) {
        // Carry WHICH of the two silences this was. They look identical on
        // screen and are not the same thing: a floor that rejected everything
        // is retrieval declining, and an empty phrase list is the lexicon
        // declining. Saying the wrong one is a small lie in the middle of the
        // demonstration.
        setDeclined({ lattice: packByKey(packKey)?.label ?? pack.manifest.persona, retrieved: facts.length });
        return;
      }
      if (!imageLlm.current) {
        // Refuse before the download rather than after it, exactly as the chat
        // path does. 2.4 GB is a long way to travel to find out.
        if (caps) {
          const verdict = canRun(janus, caps);
          if (!verdict.runnable) {
            setError(verdict.reason ?? `${janus.label} cannot run here.`);
            return;
          }
        }
        const fit = await willFit(janus.sizeBytes);
        if (!fit.fits) {
          setError(fit.warning ?? "not enough storage for the image model");
          return;
        }
        setDrawStage("loading the image model, 2.4 GB the first time");
        setLoad({ phase: "fetch", title: janus.label, pct: 0 });
        const client = new MindClient();
        await client.load("janus-pro-1b", {
          onMessage: (m) => setDrawStage(m),
          onProgress: (file, loaded, total) =>
            setLoad({
              phase: "fetch",
              title: janus.label,
              file,
              loaded,
              total,
              pct: total > 0 ? (loaded / total) * 100 : 0,
            }),
        });
        imageLlm.current = client;
        setImageReady(true);
        setLoad({ phase: null, title: "", pct: 0 });
        refreshUsage();
      }

      /* 3. DRAW. */
      const one = async (
        label: string,
        packKey: string | null,
        prompt: string,
        added: string[],
        scene?: { raw: string; overlap: number },
      ) => {
        setDrawStage(`drawing ${label}`);
        const { images, ms } = await imageLlm.current!.generateImage(prompt, { count: 1 });
        if (!images[0]) throw new Error("the image model returned no picture");
        setDrawings((prev) => [
          ...prev,
          {
            label, packKey, subject, prompt, added, scene,
            url: toDataURL(images[0]), sec: +(ms / 1000).toFixed(1),
          },
        ]);
      };

      if (drawBare) await one("no memory", null, subject, []);

      /* INTERPRET, if asked. The model reads what was remembered and composes a
       * scene, instead of the lexicon scanning for adjectives.
       *
       * Attempted BEFORE the literal path and falls through to it on failure,
       * so a lattice that would have declined still gets its chance and nothing
       * is lost when the interpretation comes back unusable. */
      let interpreted = null;
      if (interpret && facts.length > 0) {
        /* READ WITH THE BEST MODEL PRESENT, draw with the image model.
         *
         * Janus can do both, and its first attempt at this was measurably poor:
         * asked to read Hermetic facts it returned "dark room / dark green
         * wall", 0% of it traceable to anything retrieved. That is the same
         * weakness the bench found, a small text head, and it is the reason
         * Janus is not a chat tier.
         *
         * The chat model, when one is loaded, is the model this bay already
         * measured at 90% grounded on exactly this task: reading a memory
         * banner. Using it costs NOTHING extra, because it is already resident,
         * and the visitor is told that loading one improves the reading. */
        const reader = llm.current ?? imageLlm.current!;
        setDrawStage(
          llm.current ? `${entry.label} is reading what it remembers` : "reading what it remembers",
        );
        interpreted = await interpretScene(reader, subject, facts);
      }

      if (interpreted) {
        await one(
          packByKey(packKey)?.label ?? pack.manifest.persona,
          packKey,
          interpreted.prompt,
          [],
          interpreted,
        );
      } else if (built.unchanged) {
        // Say so rather than drawing the same prompt twice and presenting two
        // identical pictures as a comparison.
        // Carry WHICH of the two silences this was. They look identical on
        // screen and are not the same thing: a floor that rejected everything
        // is retrieval declining, and an empty phrase list is the lexicon
        // declining. Saying the wrong one is a small lie in the middle of the
        // demonstration.
        setDeclined({ lattice: packByKey(packKey)?.label ?? pack.manifest.persona, retrieved: facts.length });
      } else {
        await one(packByKey(packKey)?.label ?? pack.manifest.persona, packKey, built.prompt, built.added);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDrawing(false);
      setDrawStage("");
      setLoad({ phase: null, title: "", pct: 0 });
    }
  }, [pack, packKey, imgQuery, drawBare, drawing, interpret, caps, refreshUsage]);

  /**
   * Leaving picture mode gives back the 2.4 GB.
   *
   * Weights stay in the browser's cache, so coming back costs a compile and not
   * a download. What is released is GPU memory, which is the resource a visitor
   * on a laptop actually runs out of.
   */
  const leavePicture = useCallback(() => {
    imageLlm.current?.dispose();
    imageLlm.current = null;
    setImageReady(false);
  }, []);

  /* ------------------------------------------------------- the debate */

  /**
   * Load the second lattice, on demand.
   *
   * Kept out of `startDebate` so the agenda round and the discussion can each
   * ask for it without a visitor who opens the panel and closes it again ever
   * paying for a download they did not use.
   */
  const speakersFor = useCallback(async (): Promise<[Speaker, Speaker] | null> => {
    const other = packByKey(debateBKey);
    if (!pack || !other || !llm.current || !embedder.current) return null;
    setLoad({ phase: "fetch", title: other.persona, pct: 0 });
    try {
      const packB = await loadPack(other.url, {
        passphrase: other.passphrase,
        onProgress: (loaded, total) =>
          setLoad({ phase: "fetch", title: other.persona, loaded, total, pct: (loaded / total) * 100 }),
      });
      // One embedder serves both: the packs name the same model, and the
      // aperture checks the dimensions, so a mismatch cannot pass silently.
      layOut(packKey, pack);
      layOut(other.key, packB);
      return [
        { key: packKey, persona: pack.manifest.persona, pack },
        { key: other.key, persona: packB.manifest.persona, pack: packB },
      ];
    } finally {
      setLoad({ phase: null, title: "", pct: 0 });
    }
  }, [debateBKey, pack, packKey]);

  /**
   * Lay a lattice out, once.
   *
   * Synchronous and on the main thread, which is a real cost: 100ms for the
   * small packs and 1.4s for webdev's 9,886 facts, measured. It happens while
   * the second lattice is downloading, so it lands inside a wait the visitor is
   * already in rather than adding a new one.
   */
  const layOut = useCallback((key: string, p: LoadedPack) => {
    setFields((prev) => {
      if (prev[key]) return prev;
      const rows = dequantizeAll(
        p.vectors.data,
        p.vectors.scales,
        p.vectors.count,
        p.vectors.dim,
      );
      return { ...prev, [key]: project2d(rows, p.vectors.count, p.vectors.dim) };
    });
  }, []);

  /**
   * Step one: both sides propose what is worth arguing about.
   *
   * The visitor picks, and that is not decoration. Each side draws its
   * questions from what it actually remembers, so the list is the first honest
   * look at what these two memories can and cannot discuss, before a single
   * argument has been made.
   */
  const proposeQuestions = useCallback(async () => {
    const topic = debateTopic.trim();
    if (!topic) return;
    setError(null);
    setAgenda(null);
    setPicked([]);
    setDebateStream([]);
    setDebatePhase("agenda");
    setDebating(true);
    try {
      const pair = await speakersFor();
      if (!pair) throw new Error("could not load the second lattice");
      const res = await proposeAgenda(llm.current!, embedder.current!, pair[0], pair[1], topic, {
        config: cfg,
      });
      setAgenda(res);
      // Pre-select the first question so a visitor who just wants to watch can
      // press start. Choosing is an invitation, not a chore.
      setPicked(res.questions.slice(0, 1).map((q) => q.text));
      setDebatePhase("picking");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setDebatePhase("idle");
    } finally {
      setDebating(false);
    }
  }, [debateTopic, speakersFor, cfg]);

  const startDebate = useCallback(async () => {
    const topic = debateTopic.trim();
    if (!topic || picked.length === 0) return;
    setError(null);
    setDebateStream([]);
    setDebateNow(null);
    setTouches({});
    debateStop.current = { stopped: false };
    setDebatePhase("running");
    setDebating(true);

    try {
      const pair = await speakersFor();
      if (!pair) throw new Error("could not load the second lattice");
      await runStagedDebate(llm.current!, embedder.current!, pair[0], pair[1], topic, picked, {
        config: cfg,
        maxExchanges: debateRounds,
        // ON for the visitor, unlike the harness default.
        //
        // With it off, the staging pass runs with no lattice at all: the two
        // inputs are 64.5% byte-identical, and identical weights on a
        // near-identical prompt converge. Both sides opened with the literal
        // string "The Root Network." on the run that was measured, and the
        // Gardener invented a mystical lineage because it was asked what its
        // tradition holds with nothing in front of it. Free flew both arms and
        // called it: retrieval on reads much better. It costs one extra search
        // per speaker, once per discussion.
        stagingRetrieval: true,
        control: debateStop.current,
        onQuestion: (question) =>
          setDebateStream((prev) => [...prev, { kind: "question", text: question }]),
        // Which facts each side actually reached for, per turn. The one thing
        // about an agent's state that is measured rather than imagined.
        onStatement: (st) =>
          setTouches((prev) => ({
            ...prev,
            [st.speakerKey]: [
              ...(prev[st.speakerKey] ?? []),
              { rows: st.rowsTouched, at: performance.now() },
            ],
          })),
        onRebuttal: (rb) =>
          setTouches((prev) => ({
            ...prev,
            [rb.speakerKey]: [
              ...(prev[rb.speakerKey] ?? []),
              { rows: rb.rowsTouched, at: performance.now() },
            ],
          })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDebating(false);
      setDebateNow(null);
      setDebateWait(0);
      setDebatePhase("picking");
      setLoad({ phase: null, title: "", pct: 0 });
    }
  }, [debateTopic, picked, speakersFor, debateRounds, cfg]);

  const stopDebate = useCallback(() => {
    debateStop.current.stopped = true;
  }, []);

  /* -------------------------------------------------------------- speak */

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy) return;

    // Slash commands are handled here, before anything touches the model, so
    // they work even when nothing is loaded.
    if (text === "/clear" || text === "/reset") {
      setTurns([]);
      setProv([]);
      setError(null);
      setLastMs(null);
      setDraft("");
      // The KV cache holds the old conversation's prefix. Leaving it would let
      // a cleared window still condition on what it supposedly forgot.
      llm.current?.resetCache();
      return;
    }
    if (text === "/help") {
      setDraft("");
      setError("/clear wipes the conversation and its context. /help shows this.");
      return;
    }

    if (!llm.current || !modelReady) return;

    setDraft("");
    setError(null);
    setProv([]);
    const next: Turn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setBusy("gen");
    liveText.current = "";
    if (liveRef.current) liveRef.current.textContent = "";
    requestAnimationFrame(scrollDown);

    // Only completed turns go into history. The banner and tool records are
    // rebuilt each turn by converse() rather than accumulating in the
    // transcript, which keeps the context small on purpose.
    const history: ChatMessage[] = turns.map((t) => ({ role: t.role, content: t.content }));

    try {
      const res = await converse(
        llm.current,
        embedder.current!,
        embedReady ? pack : null,
        history,
        text,
        cfg,
        (ev) => {
          if (ev.type === "token") {
            liveText.current += ev.text;
            if (liveRef.current) liveRef.current.textContent = liveText.current;
            scrollDown();
          } else if (ev.type === "prefetch") {
            setProv((p) => [...p, { source: "prefetch", results: ev.result.results, ms: ev.result.embedMs + ev.result.searchMs }]);
          } else if (ev.type === "toolResult") {
            setProv((p) => [...p, { source: "tool", results: ev.result.results, ms: ev.result.embedMs + ev.result.searchMs }]);
          } else if (ev.type === "toolCall") {
            setProv((p) => [...p, { source: "tool", query: ev.query, results: [], ms: 0 }]);
          }
        },
      );
      setTurns([...next, { role: "assistant", content: res.answer }]);
      setLastMs(res.totalMs);
      liveText.current = "";
      if (liveRef.current) liveRef.current.textContent = "";
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Roll the question back out of the transcript. Leaving it stranded
      // means a retry shows the same question twice, which is what happened
      // the first time this path was exercised.
      setTurns(turns);
      setDraft(text);
    } finally {
      setBusy(null);
      requestAnimationFrame(scrollDown);
    }
  }, [draft, modelReady, busy, turns, pack, embedReady, cfg]);

  const entry = modelByKey(modelKey);
  const canSend = !busy && draft.trim().length > 0 && (modelReady || draft.trim().startsWith("/"));
  /**
   * Drawing needs a lattice and an embedder, and NOT the chat model.
   *
   * Deliberately not `modelReady`: the image engine is its own client, so a
   * visitor who came straight to this mode should not be made to download 764 MB
   * of a model that will take no part in the picture.
   */
  const canDraw = !busy && !drawing && !!pack && embedReady && draft.trim().length > 0;

  return (
    <div className="mind-shell">
      {/* Outside the panels on purpose: the wait belongs to the whole page, and
          a visitor should not have to find the sidebar to learn what is
          happening. */}
      <LoadingOverlay state={load} />
      <header className="mind-head">
        <span className="mind-title">The Mind</span>
        <span className="mind-sub">
          {pack ? pack.manifest.persona : "a small model, running in your browser, over frozen memory"}
        </span>
        <a className="mind-home" href="https://www.free.vet">
          free.vet
        </a>
      </header>

      {/* THREE THINGS, named as a person would name them.
          Not "modes" and not verbs from the code: a visitor should be able to
          tell what each one does without being told what a lattice is. */}
      {gpu?.supported && (
        <nav className="modes" aria-label="What to do">
          {([
            ["ask", "Ask an expert", "one memory, answering from it"],
            ["two", "Two experts", "one model, two memories, disagreeing"],
            ["picture", "Picture it", "draw it from what this memory knows"],
          ] as const).map(([key, label, note]) => (
            <button
              key={key}
              className="mode-tab"
              aria-pressed={mode === key}
              disabled={!!busy || debating || drawing}
              title={note}
              onClick={() => {
                if (mode === key) return;
                // Entering anything other than picture mode hands back the
                // image model's GPU memory. Cheap to do, and easy to forget.
                if (mode === "picture") leavePicture();
                setMode(key);
                setError(null);
              }}
            >
              <span className="mode-name">{label}</span>
              <span className="mode-note">{note}</span>
            </button>
          ))}
        </nav>
      )}

      {gpu && (
        <div className="caps">
          <span>
            <i className={`dot ${gpu.supported ? "on" : "off"}`} />
            WebGPU <b>{gpu.supported ? "ready" : "unavailable"}</b>
          </span>
          <span>
            <i className={`dot ${modelReady ? "on" : "off"}`} />
            model <b>{modelReady ? entry.label : "not loaded"}</b>
          </span>
          <span>
            <i className={`dot ${pack && embedReady ? "on" : "off"}`} />
            lattice{" "}
            <b>
              {pack
                ? `${packByKey(packKey)?.label ?? pack.manifest.persona} · ${pack.facts.length.toLocaleString()} facts`
                : "none"}
            </b>
          </span>
          <span>
            cached <b>{formatBytes(usage?.usageBytes ?? gpu.usageBytes)}</b> of{" "}
            <b>{formatBytes(usage?.quotaBytes ?? gpu.quotaBytes)}</b>
            {(usage?.usageBytes ?? 0) > 1e6 && (
              <button
                className="linkish"
                disabled={!!busy}
                title="Delete every downloaded model and embedder from this browser"
                onClick={() => {
                  void clearWeightCache().then(refreshUsage);
                }}
              >
                clear
              </button>
            )}
          </span>
          {loadMs !== null && (
            <span>
              model load <b>{(loadMs / 1000).toFixed(1)}s</b>
            </span>
          )}
          {lastMs !== null && (
            <span>
              last turn <b>{(lastMs / 1000).toFixed(1)}s</b>
            </span>
          )}
        </div>
      )}

      {gpu && !gpu.supported ? (
        <div className="gate">
          <h2>This page needs WebGPU</h2>
          <p>{gpu.reason}</p>
          <p>
            The Mind runs a language model entirely on your own graphics card. Nothing you type is sent anywhere, because
            there is no server to send it to. That needs WebGPU, which is in current Chrome, Edge, Firefox on Windows, and
            Safari 26 and later including iOS.
          </p>
          <p>
            Everything else on <a href="https://www.free.vet">free.vet</a> works without it.
          </p>
        </div>
      ) : (
        <div className="mind-body">
          <aside className="panel side">
            <h3 className="fold" onClick={() => setMemoryOpen((v) => !v)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setMemoryOpen((v) => !v); }}>
              <span className={`caretmark ${memoryOpen ? "open" : ""}`} />
              Memory
              {!memoryOpen && pack && <span className="fold-sum">{packByKey(packKey)?.label}</span>}
            </h3>
            {memoryOpen &&
              PACKS.map((p) => (
              <button
                key={p.key}
                className="model-row"
                aria-pressed={p.key === packKey && !!pack}
                disabled={!!busy}
                onClick={() => void openPack(p.key)}
              >
                <span className="name">
                  <span>{p.label}</span>
                  <span className="size">{p.factCount.toLocaleString()} facts</span>
                </span>
                  <span className="note">{p.note}</span>
                </button>
              ))}

            <h3 className="fold" style={{ marginTop: 18 }} onClick={() => setModelOpen((v) => !v)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setModelOpen((v) => !v); }}>
              <span className={`caretmark ${modelOpen ? "open" : ""}`} />
              Model
              {!modelOpen && modelReady && <span className="fold-sum">{entry.label}</span>}
            </h3>
            {modelOpen &&
              SHIPPED_MODELS.map((m) => {
                // Asked of the adapter, so the answer is about THIS device.
                const verdict = caps ? canRun(m, caps) : null;
                const blocked = verdict ? !verdict.runnable : false;
                return (
                  <button
                    key={m.key}
                    className={`model-row${blocked ? " unavailable" : ""}`}
                    aria-pressed={m.key === modelKey && modelReady}
                    disabled={!!busy || blocked}
                    title={verdict?.reason ?? verdict?.warning ?? undefined}
                    onClick={() => void openModel(m.key)}
                  >
                    <span className="name">
                      <span>{m.label}</span>
                      <span className="size">{blocked ? "unavailable" : formatBytes(m.sizeBytes)}</span>
                    </span>
                    {/* The reason replaces the sales pitch: a visitor who cannot
                        run something is owed the why, not the description. */}
                    <span className="note">{blocked ? verdict?.reason : m.note}</span>
                    {!blocked && verdict?.warning && <span className="note warn">{verdict.warning}</span>}
                  </button>
                );
              })}

            {busy && (
              <>
                <div className="bar">
                  <i style={{ width: `${pct.toFixed(1)}%` }} />
                </div>
                <div className="progress-line">{loadMsg || "working"}</div>
              </>
            )}

            {error && <div className="err">{error}</div>}

            {/* PICTURE IT. The controls a drawing needs, and nothing else.
                The subject itself goes in the composer, because typing what you
                want is the same gesture as asking a question and it should not
                move to the sidebar just because the output is pixels. */}
            {mode === "picture" && (
              <section className="tuning open">
                <h4>Picture it</h4>
                <p className="hint">
                  The same words drawn twice: once on their own, once with what this
                  memory knows about them. If the two pictures differ, the memory did it.
                </p>
                <label className="toggle">
                  look up
                  <input
                    className="debate-topic"
                    placeholder="(optional) search words"
                    value={imgQuery}
                    onChange={(e) => setImgQuery(e.target.value)}
                    disabled={drawing}
                  />
                </label>
                {/* Worth its own line: what to SEARCH for need not be what to
                    DRAW, and that is not obvious until someone says it. */}
                <p className="hint">
                  Leave it empty to search for what you asked to draw. Fill it in when the
                  two differ: draw <i>a tray of seedlings</i>, look up <i>damping-off</i>,
                  and it draws a sick one.
                </p>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={interpret}
                    onChange={(e) => setInterpret(e.target.checked)}
                    disabled={drawing}
                  />
                  let it interpret what it remembers
                </label>
                <p className="hint">
                  On, the mind reads what it recalled and composes a scene. Off, it only
                  picks out words that describe appearance, which is quick and exact but
                  blind to imagery that lives in nouns: the Hermeticist has just 11 such
                  words in 597 facts, and 63 of the hits are the <i>emerald</i> in Emerald
                  Tablet. Interpreting costs a few seconds and invents more freely.
                  {/* The reading and the drawing are different jobs and want
                      different models. Say so, because it is a real quality
                      difference the visitor controls and would not guess. */}
                  {" "}{modelReady
                    ? <><b>{entry.label}</b> is loaded, so it will do the reading.</>
                    : <>The image model will do the reading on its own, which is rough and
                      often charming. If you load a chat model it takes over that part and
                      writes better scenes, but nothing here needs it.</>}
                </p>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={drawBare}
                    onChange={(e) => setDrawBare(e.target.checked)}
                    disabled={drawing}
                  />
                  also draw it with no memory
                </label>
                <p className="hint">
                  The image model is <b>Janus Pro 1B</b>, 2.4 GB, separate from the one that
                  talks. It downloads the first time you draw and is released when you leave
                  this mode. About 13 seconds a picture.
                  {imageReady && <> <b>Loaded.</b></>}
                </p>
                {drawings.length > 0 && (
                  <button className="ghost tiny" disabled={drawing} onClick={() => setDrawings([])}>
                    clear the pictures
                  </button>
                )}
                {!pack && <p className="hint">Load a lattice first.</p>}
              </section>
            )}

            {mode === "two" && (
            <section className="tuning open">
              <h4>Two experts</h4>
              {/* The clearest demonstration of the whole thesis: the model never
                  changes, only the memory, and the two disagree because they
                  remember different things. */}
              <p className="hint">
                One model, two lattices. They discuss a topic you choose, and disagree from
                what each of them remembers.
              </p>
              <input
                className="debate-topic"
                placeholder="a topic for them to argue about"
                value={debateTopic}
                onChange={(e) => setDebateTopic(e.target.value)}
                disabled={debating}
              />
              <label className="toggle">
                against
                <select
                  value={debateBKey}
                  onChange={(e) => setDebateBKey(e.target.value)}
                  disabled={debating}
                >
                  <option value="">choose a second lattice</option>
                  {PACKS.filter((p) => p.key !== packKey).map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.persona}
                    </option>
                  ))}
                </select>
              </label>
              <label className="toggle">
                turns each
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={debateRounds}
                  onChange={(e) => setDebateRounds(Math.max(1, Math.min(8, +e.target.value || 1)))}
                  disabled={debating}
                />
              </label>
              <label className="toggle">
                pause {(debateDelay / 1000).toFixed(1)}s
                <input
                  type="range"
                  min={0}
                  max={6000}
                  step={250}
                  value={debateDelay}
                  onChange={(e) => setDebateDelay(+e.target.value)}
                />
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showField}
                  onChange={(e) => setShowField(e.target.checked)}
                />
                show their memory as a field
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showNotes}
                  onChange={(e) => setShowNotes(e.target.checked)}
                />
                show what each of them is thinking
              </label>

              {/* TWO STEPS, and the first one is not a formality. Each side
                  draws its questions from what it actually remembers, so the
                  list is the first honest look at what these two memories can
                  and cannot discuss, before an argument has been made. */}
              {debatePhase !== "picking" ? (
                <button
                  className="debate-go"
                  disabled={!pack || !modelReady || !debateBKey || !debateTopic.trim() || !!busy}
                  onClick={() => (debating ? stopDebate() : void proposeQuestions())}
                >
                  {debating ? "stop" : "ask them what is worth arguing about"}
                </button>
              ) : (
                <>
                  <p className="hint">
                    What each of them wants to argue about. Pick one or more.
                  </p>
                  <ul className="agenda">
                    {(agenda?.questions ?? []).map((q) => (
                      <li key={q.text}>
                        <label>
                          <input
                            type="checkbox"
                            checked={picked.includes(q.text)}
                            disabled={debating}
                            onChange={(e) =>
                              setPicked((prev) =>
                                e.target.checked
                                  ? [...prev, q.text]
                                  : prev.filter((x) => x !== q.text),
                              )
                            }
                          />
                          <span>{q.text}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {/* When a side proposes nothing, say so rather than letting an
                      empty list read as a bug. It is a real finding about that
                      lattice: it has nothing to say on this topic. */}
                  {Object.values(agenda?.health ?? {})
                    .filter((h) => !h.ok)
                    .map((h) => (
                      <p className="hint" key={h.persona}>
                        {h.persona} could not find much on this in what it remembers.
                      </p>
                    ))}
                  <button
                    className="debate-go"
                    disabled={picked.length === 0 || !!busy}
                    onClick={() => (debating ? stopDebate() : void startDebate())}
                  >
                    {debating
                      ? "stop"
                      : `start the discussion (${picked.length} question${picked.length === 1 ? "" : "s"})`}
                  </button>
                  <button className="ghost tiny" disabled={debating} onClick={() => setDebatePhase("idle")}>
                    different topic
                  </button>
                </>
              )}
              {!modelReady && <p className="hint">Load a model and a lattice first.</p>}
            </section>
            )}

            <details className="tuning">
              <summary>Tuning</summary>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={cfg.enableThinking}
                  onChange={(e) => setCfg({ ...cfg, enableThinking: e.target.checked })}
                />
                reasoning mode (off by default, measurably better off for memory work)
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={cfg.prefetch}
                  onChange={(e) => setCfg({ ...cfg, prefetch: e.target.checked })}
                />
                prefetch memory before answering
              </label>
              {/* HOW SURE A MEMORY HAS TO BE. Measured 2026-07-27: at the old
                  0.45 this rejected nothing at all, so a question the lattice
                  could not answer still got six confident irrelevant records
                  above the words "answer using only what is above". 0.62 keeps
                  87% of right answers and admits 3% of unanswerable ones. */}
              <label className="toggle">
                how sure a memory must be, {(cfg.floor ?? RELEVANCE_FLOOR).toFixed(2)}
                <input
                  type="range"
                  min={0.4}
                  max={0.85}
                  step={0.01}
                  value={cfg.floor ?? RELEVANCE_FLOOR}
                  onChange={(e) => setCfg({ ...cfg, floor: +e.target.value })}
                />
              </label>
              <label className="toggle">
                tool call
                <select
                  value={cfg.arm}
                  onChange={(e) => setCfg({ ...cfg, arm: e.target.value as JustifyArm })}
                >
                  <option value="direct">direct</option>
                  <option value="justify">interrupt and justify</option>
                  <option value="justify-escape">justify, with escape</option>
                </select>
              </label>
              <label className="toggle">
                dialect
                <select
                  value={cfg.dialect}
                  onChange={(e) => setCfg({ ...cfg, dialect: e.target.value as "records" | "tag" })}
                >
                  <option value="records">records</option>
                  <option value="tag">tag</option>
                </select>
              </label>
            </details>
          </aside>

          <main className="panel">
            <h3>
              {mode === "picture"
                ? "Pictures"
                : debateStream.length > 0 || debatePhase !== "idle"
                  ? "Discussion"
                  : "Conversation"}
            </h3>
            <div className="chat" ref={chatRef}>
              {/* THE PICTURES, newest last, so a strip built up over several
                  subjects reads in the order it was made.

                  Each card carries the prompt that produced it and the phrases
                  the lattice contributed, because the claim is not "here is a
                  picture" but "this memory changed this picture", and that is
                  only checkable if the words are on screen next to it. */}
              {mode === "picture" && (
                <>
                  {drawings.map((d, i) => (
                    <figure key={i} className={`drawing ${d.packKey ? "grounded" : "bare"}`}>
                      <img src={d.url} alt={d.prompt} width={384} height={384} />
                      <figcaption>
                        <div className="who">
                          {d.label}
                          <span className="ms"> {d.sec}s</span>
                        </div>
                        <div className="body">{d.prompt}</div>
                        {d.added.length > 0 && (
                          <div className="added">
                            {/* Named one by one rather than summarised, so a
                                phrase that came out wrong is visible as a
                                phrase and not buried in a count. */}
                            remembered: {d.added.map((a, j) => <b key={j}>{a}</b>)}
                          </div>
                        )}
                        {/* HOW MUCH OF THIS CAME FROM THE MEMORY.
                            An interpretation cannot list the phrases it used
                            the way the lexicon can, so it reports the share of
                            its words that trace back to the retrieved facts. A
                            low number is not an error and is not hidden: it
                            means the mind took the memory as a starting point
                            and went somewhere of its own. */}
                        {d.scene && (
                          <div className="added">
                            interpreted, <b>{Math.round(d.scene.overlap * 100)}%</b> of it
                            traceable to what it recalled
                            {d.scene.overlap < 0.15 && <> {"·"} mostly its own invention</>}
                            {/* THE ACTIONABLE LEVER, and it is the retrieval
                                finding in miniature. Asked to draw "the emerald
                                tablet", the lattice returned which manuscript it
                                first appears in: what the subject IS ABOUT, not
                                what it LOOKS LIKE. The look-up box is the one
                                control that changes which facts arrive, so a low
                                score should point at it rather than just scold. */}
                            {d.scene.overlap < 0.15 && (
                              <div className="added none">
                                What it recalled was about the subject rather than the look of
                                it. Put appearance words in <i>look up</i> to pull different
                                facts: not the name of the thing, but what it is made of, what
                                colour it is, where it sits.
                              </div>
                            )}
                          </div>
                        )}
                        {!d.packKey && <div className="added none">your words alone, no memory</div>}
                      </figcaption>
                    </figure>
                  ))}
                  {/* A lattice that declined, shown where its picture would
                      have been. This is the honest outcome and it gets the
                      same weight as a drawing, not a red box in the sidebar. */}
                  {declined && !drawing && (
                    <div className="declined">
                      {declined.retrieved === 0 ? (
                        <>
                          <b>{declined.lattice}</b> had nothing to say about that at all:
                          nothing it remembers came back above the relevance floor. That is
                          the memory correctly recognising a subject outside it.
                        </>
                      ) : (
                        <>
                          <b>{declined.lattice}</b> remembered {declined.retrieved} things about
                          that, but none of them describe how anything <i>looks</i>. Real facts,
                          nothing to draw with.
                        </>
                      )}{" "}
                      A memory that can decline is the point: one that always found something
                      would be decorating your words rather than grounding them.
                      {/* Do not assert a picture count. With the control arm off
                          and a lattice that declines there are none, and saying
                          "only the one picture" was wrong in exactly that case. */}
                      {!drawBare && (
                        <> Switch on <i>also draw it with no memory</i> to see it drawn from
                        your words alone.</>
                      )}
                    </div>
                  )}
                  {drawing && <div className="hint debate-wait">{drawStage || "working"}</div>}
                  {drawings.length === 0 && !drawing && (
                    <div className="hint">
                      Type what you want to see. It draws twice: once from your words alone,
                      and once from your words plus what this memory knows about them.
                      {/* MEASURED suggestions, not plausible ones. The share of
                          each lattice carrying any word an image model can use:
                          printing 23.2%, gardening 16.7%, hermes 8.5%, webdev
                          1.7%, spark-kit 0.8%. The old copy here recommended the
                          Spark Expert, which declines about 94% of the time. */}
                      {" "}The <b>Printer</b> and the <b>Gardener</b> have the most to look at:
                      ask for a spool of filament, or a tray of seedlings. The technical
                      lattices describe how things work rather than how they look, and will
                      mostly decline. Nothing is sent anywhere, and the pictures never leave
                      this tab.
                    </div>
                  )}
                </>
              )}
              {mode !== "picture" && (
              <>
              {/* The debate transcript takes over the same column: two experts
                  reading from different memory is the thing worth looking at,
                  and splitting it into its own pane would bury it. */}
              {(debateStream.length > 0 || debatePhase === "running") && (
                <>
                  {/* ONE INTERPRETER, TWO MEMORIES, drawn as exactly that.
                      The model node is rendered ONCE and feeds both fields,
                      because there IS one model: drawing it twice would be a
                      small lie in the middle of the demonstration. */}
                  {showField && (
                    <div className="minds">
                      <div className="interpreter">
                        <span className="cap">one interpreter</span>
                        <span className="nm">{entry?.label ?? modelKey}</span>
                      </div>
                      <div className="feeds" aria-hidden="true">
                        <i />
                        <i />
                      </div>
                      <div className="fields">
                        <AgentField
                          projection={fields[packKey] ?? null}
                          touches={touches[packKey] ?? []}
                          label={pack?.manifest.persona ?? "A"}
                          detail={`${pack?.facts.length ?? 0} facts`}
                          tone="#ff7a1a"
                          running={debating}
                        />
                        <AgentField
                          projection={fields[debateBKey] ?? null}
                          touches={touches[debateBKey] ?? []}
                          label={packByKey(debateBKey)?.persona ?? "B"}
                          detail={`${fields[debateBKey]?.count ?? 0} facts`}
                          tone="#e08a3c"
                          running={debating}
                        />
                      </div>
                      <p className="hint field-note">
                        Every dot is one remembered fact, placed near the facts it resembles.
                        They light up as each expert reaches for them. Nearness is indicative:
                        two dimensions of a much larger space.
                      </p>
                    </div>
                  )}
                  {debateStream.map((item, i) =>
                    item.kind === "question" ? (
                      <div key={`q${i}`} className="debate-question">{item.text}</div>
                    ) : item.kind === "note" ? (
                      showNotes && (
                        <div
                          key={`n${i}`}
                          className={`turn debate note ${item.speakerKey === packKey ? "a" : "b"}`}
                        >
                          <div className="who">{item.persona} is thinking</div>
                          <div className="body">{item.text}</div>
                        </div>
                      )
                    ) : (
                      <div
                        key={`s${i}`}
                        className={`turn debate ${item.speakerKey === packKey ? "a" : "b"}`}
                      >
                        <div className="who">{item.persona}</div>
                        <div className="body">{item.text}</div>
                      </div>
                    ),
                  )}
                  {/* Something is always happening, and saying which is cheaper
                      than a spinner and truer than one. A staged turn is three
                      generations, so a silent gap here runs to several seconds
                      and reads as a hang. */}
                  {debateNow && (
                    <div className="hint debate-wait">
                      {debateNow.persona} is {debateNow.kind === "think" ? "thinking" : "answering"}
                    </div>
                  )}
                  {debateWait > 0 && !debateNow && (
                    <div className="hint debate-wait">a pause, {(debateWait / 1000).toFixed(1)}s</div>
                  )}
                  {!debating && debateStream.length > 0 && (
                    <div className="hint">
                      Same model throughout. Only the memory changed.
                    </div>
                  )}
                </>
              )}
              {debateStream.length === 0 && !debating && turns.map((t, i) => (
                <div key={i} className={`turn ${t.role}`}>
                  <div className="who">{t.role === "user" ? "you" : pack ? pack.manifest.persona : "the mind"}</div>
                  <div className="body">{t.content}</div>
                </div>
              ))}
              {busy === "gen" && (
                <div className="turn assistant">
                  <div className="who">{pack ? pack.manifest.persona : "the mind"}</div>
                  <div className="body caret" ref={liveRef} />
                </div>
              )}
              {turns.length === 0 && !busy && (
                <div className="hint">
                  Load a lattice, then a model. The lattice is small and quick; the model is a few hundred megabytes the
                  first time, then it is cached and later visits are fast. Ask the Spark Expert about the GB10, or the
                  Gardener about seed timing. Nothing you type leaves this tab.
                </div>
              )}
              </>
              )}
            </div>

            {/* ONE COMPOSER for all three modes. Typing what you want is the
                same gesture whether the answer comes back as words or as a
                picture, and giving the image path its own input box would have
                made it look like a different application. */}
            <div className="composer">
              <textarea
                value={draft}
                placeholder={
                  mode === "picture"
                    ? pack
                      ? "Describe what to draw"
                      : "Load a lattice first"
                    : modelReady
                      ? "Ask it something, or /clear"
                      : "Load a model first"
                }
                disabled={!!busy || drawing}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (mode === "picture") {
                      if (canDraw) { const s = draft.trim(); setDraft(""); void draw(s); }
                    } else void send();
                  }
                }}
              />
              {mode === "picture" ? (
                <button
                  className="go"
                  disabled={!canDraw}
                  onClick={() => { const s = draft.trim(); setDraft(""); void draw(s); }}
                >
                  {drawing ? "drawing" : "draw"}
                </button>
              ) : (
                <button className="go" disabled={!canSend} onClick={() => void send()}>
                  send
                </button>
              )}
            </div>
          </main>

          <aside className="panel side">
            <h3>Provenance</h3>
            <div className="chat">
              {prov.length === 0 && (
                <div className="hint">
                  Every fact the model is shown appears here, with the quote and reference it came from. If this panel is
                  empty, the answer was not grounded in memory.
                </div>
              )}
              {prov.map((p, i) => (
                <div key={i} className="prov">
                  <div className="who">
                    {p.source === "prefetch" ? "prefetch" : `search: ${p.query ?? ""}`}
                    {p.ms > 0 && <span className="ms"> {p.ms.toFixed(0)} ms</span>}
                  </div>
                  {p.results.length === 0 && p.source === "prefetch" && <div className="none">nothing above the relevance floor</div>}
                  {p.results.map((r, j) => (
                    <div key={j} className="rec">
                      <div className="fact">{r.fact}</div>
                      {r.quote && <div className="quote">{r.quote}</div>}
                      <div className="meta">
                        {r.ref && <span>{r.ref}</span>}
                        {r.score !== undefined && <span>{r.score.toFixed(3)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
